/**
 * Application Constants
 * Centralized constants for forms, dropdowns, and configurations
 */

// ===========================================
// Property Constants
// ===========================================

export const PROPERTY_TYPES = [
    { value: '', label: 'Select Type' },
    { value: 'Single Family', label: 'Single Family' },
    { value: 'Condo', label: 'Condo' },
    { value: 'Townhouse', label: 'Townhouse' },
    { value: 'Multi Family', label: 'Multi Family' },
    { value: 'Land', label: 'Land' },
    { value: 'Commercial', label: 'Commercial' },
] as const;

export const PROPERTY_STATUSES = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'sold', label: 'Sold' },
    { value: 'off_market', label: 'Off Market' },
] as const;

export const DEFAULT_STATE = 'NJ';

// ===========================================
// Contact Constants
// ===========================================

export const TITLE_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Prof.', label: 'Prof.' },
] as const;

export const SOURCE_OPTIONS = [
    { value: 'manual', label: 'Manual Entry', icon: '✏️' },
    { value: 'website', label: 'Website', icon: '🌐' },
    { value: 'open_house', label: 'Open House', icon: '🏠' },
    { value: 'referral', label: 'Referral', icon: '🤝' },
    { value: 'zillow', label: 'Zillow', icon: '🔍' },
    { value: 'realtor', label: 'Realtor.com', icon: '🏡' },
    { value: 'social', label: 'Social Media', icon: '📱' },
    { value: 'other', label: 'Other', icon: '📋' },
] as const;

export const PROFESSION_OPTIONS = [
    'Accountancy',
    'Acupuncture',
    'Architect',
    'Athletic Trainer',
    'Audiology',
    'Chiropractic',
    'Cosmetology',
    'Court Reporter',
    'Dentistry',
    'Diet and Nutrition',
    'Electrical Contractor',
    'Electrologist',
    'Engineer',
    'Genetic Counseling',
    'Hearing Aid Dispenser',
    'Home Improvement Contractor',
    'Home Inspector',
    'HVACR',
    'Interior Designer',
    'Landscape Architect',
    'Marriage Therapist',
    'Massage Therapist',
    'Master Plumber',
    'Medical Examiner',
    'Mortuary Science',
    'Nursing',
    'Occupational Therapy',
    'Optometry',
    'Pharmacy',
    'Physical Therapy',
    'Physician Assistant',
    'Psychology',
    'Real Estate Appraiser',
    'Respiratory Care',
    'Social Worker',
    'Veterinary',
    'Other',
] as const;

// Business categories used on the company-details panel. Mirrors
// the service-professional list (so the two pickers stay aligned)
// and adds "Real Estate" for brokerages / mortgage / title companies
// that don't fit any of the licensed-profession entries cleanly.
export const BUSINESS_CATEGORY_OPTIONS = [
    'Real Estate',
    ...PROFESSION_OPTIONS,
] as const;

// ===========================================
// Form Field Configurations
// ===========================================

export const PROPERTY_FORM_FIELDS = {
    location: [
        { name: 'address', label: 'Address', type: 'text', required: true, placeholder: '123 Main Street' },
        { name: 'city', label: 'City', type: 'text', required: true },
        { name: 'state', label: 'State', type: 'text', required: true },
        { name: 'zip_code', label: 'ZIP Code', type: 'text', required: false },
    ],
    details: [
        { name: 'price', label: 'Price', type: 'number', placeholder: '500000' },
        { name: 'bedrooms', label: 'Bedrooms', type: 'number' },
        { name: 'bathrooms', label: 'Bathrooms', type: 'number', step: '0.5' },
        { name: 'square_feet', label: 'Square Feet', type: 'number' },
    ],
    features: [
        { name: 'year_built', label: 'Year Built', type: 'number' },
        { name: 'lot_size', label: 'Lot Size (sqft)', type: 'number' },
        { name: 'parking', label: 'Parking', type: 'text', placeholder: 'e.g., 2 Car Garage' },
        { name: 'heating', label: 'Heating', type: 'text' },
    ],
} as const;

// ===========================================
// Validation Messages
// ===========================================

export const VALIDATION_MESSAGES = {
    required: (field: string) => `${field} is required`,
    email: 'Please enter a valid email address',
    phone: 'Please enter a valid phone number',
    minLength: (field: string, min: number) => `${field} must be at least ${min} characters`,
    maxLength: (field: string, max: number) => `${field} must be at most ${max} characters`,
    positive: (field: string) => `${field} must be a positive number`,
    integer: (field: string) => `${field} must be a whole number`,
} as const;

// ===========================================
// UI Constants
// ===========================================

export const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;
export const DEFAULT_ITEMS_PER_PAGE = 25;

// Date formatting
export const DATE_FORMAT = 'MMM dd, yyyy';
export const DATETIME_FORMAT = 'MMM dd, yyyy HH:mm';

// ===========================================
// Type Exports
// ===========================================

export type PropertyType = typeof PROPERTY_TYPES[number]['value'];
export type PropertyStatus = typeof PROPERTY_STATUSES[number]['value'];
export type TitleOption = typeof TITLE_OPTIONS[number]['value'];
export type SourceOption = typeof SOURCE_OPTIONS[number]['value'];
export type ProfessionOption = typeof PROFESSION_OPTIONS[number];
